import {
  CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  DndContext,
  MouseSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";
import { Flex, Table as RadixTable } from "@radix-ui/themes";
import { styled } from "@stitches/react";
import {
  TableOptions,
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
  RowData,
  Row as RowType,
  ColumnPinningState,
  Column,
  Cell,
  ExpandedState,
  SortingState,
  OnChangeFn,
} from "@tanstack/react-table";
import { debounce } from "lodash";
import { createPortal } from "react-dom";

import { Row } from "./Row";
import { Loader } from "../Loader";
import { Spinner } from "../Spinner";

const RadixTableRoot = styled(RadixTable.Root, {
  variants: {
    bordered: {
      true: {
        border: "1px solid var(--gray-4)",
        borderRadius: "var(--radius-2)",
      },
    },
  },
});

const getCommonExpandedRowStyles = <TData,>(
  row: RowType<TData>
): CSSProperties => {
  if (row.getIsExpanded()) {
    return {
      boxShadow: "unset",
    };
  }
  return {};
};

const getCommonPinningStyles = <TData,>(
  column: Column<TData>
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-4px 0 4px -4px var(--gray-8) inset"
      : isFirstRightPinnedColumn
      ? "4px 0 4px -4px var(--gray-8) inset"
      : isPinned
      ? "0 0 0px var(--gray-8) inset"
      : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: isPinned ? 0.98 : 1,
    position: isPinned ? "sticky" : "inherit",
    width: `${column.getSize()}px`,
    zIndex: isPinned ? 1 : 0,
    backgroundColor: isPinned ? "var(--color-panel)" : undefined,
    backdropFilter: isPinned ? "blur(12px)" : undefined,
    border: isPinned ? "none" : undefined,
  };
};

type SizeStyles = {
  width?: CSSProperties["width"];
  maxWidth?: CSSProperties["maxWidth"];
  minWidth?: CSSProperties["minWidth"];
};

declare module "@tanstack/react-table" {
  interface TableMeta<TData extends RowData> {
    onMoveRow?: {
      (row: TData, from: number, to: number): void;
    };
    onCellUpdateWithRowId?: (
      rowId: string,
      columnId: string,
      value: unknown
    ) => void;
    onTextInputCellUpdate?: (
      rowIndex: number,
      columnId: string,
      value: string
    ) => void;
    onSelectInputCellUpdate?: (
      rowIndex: number,
      columnId: string,
      value: string
    ) => void;
    onMultiSelectInputCellUpdate?: (
      rowIndex: number,
      columnId: string,
      value: string[]
    ) => void;
    getRowStyles?: (
      row: RowType<TData>,
      isHovered?: boolean
    ) => React.CSSProperties;
    getHoveredRowIndex?: () => number | undefined;
    // this is a hack to access the input value in the table before its blurred
    intermediateInput?: {
      rowIndex: number;
      columnId: string;
      value: string;
    };
    setIntermediateInput?: (value?: {
      rowIndex: number;
      columnId: string;
      value: string;
    }) => void;
    getIsLoading?: () => boolean | undefined;
    getCellSizeStyles?: (cell: Cell<TData, unknown>) => SizeStyles;
    getContainerModel?: () => Record<string, unknown>;
  }
}

type Props<TData> = Pick<
  TableOptions<TData>,
  | "columns"
  | "data"
  | "getRowId"
  | "enableColumnPinning"
  | "onColumnPinningChange"
> & {
  variant?: "surface" | "ghost";
  bordered?: boolean;
  columnPinning?: ColumnPinningState;
  expanded?: ExpandedState;
  enableSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onCellUpdate?: (rowIndex: number, columnId: string, value: unknown) => void;
  onCellUpdateWithRowId?: (
    rowId: string,
    columnId: string,
    value: unknown
  ) => void;
  getRowStyles?: (
    row: RowType<TData>,
    isHovered?: boolean
  ) => React.CSSProperties;
  onMoveRow?: (row: TData, from: number, to: number) => void;
  onCoreRowClick?: (row: RowType<TData>) => void;
  onRowClick?: (row: TData) => void;
  getRowCanExpand?: (row: RowType<TData>) => boolean;
  renderExpandedRow?: (row: RowType<TData>) => React.ReactNode;
  renderUnwrappedExpandedRow?: (row: RowType<TData>) => React.ReactNode;
  getIsLoading?: () => boolean | undefined;
  getCellSizeStyles?: (cell: Cell<TData, unknown>) => SizeStyles;
  getContainerModel?: () => Record<string, unknown>;
  hasMore?: boolean;
  onLoadMore?: () => void;
};

export const Table = <TData extends RowData>({
  columns,
  data,
  columnPinning,
  expanded,
  variant = "surface",
  bordered = true,
  enableSorting = false,
  sorting,
  onSortingChange,
  onCellUpdate,
  onCellUpdateWithRowId,
  getRowStyles,
  getRowId,
  onMoveRow,
  onCoreRowClick,
  onRowClick,
  getRowCanExpand,
  renderExpandedRow,
  renderUnwrappedExpandedRow,
  getIsLoading,
  getCellSizeStyles: getCellSizeStylesFromProps,
  getContainerModel,
  hasMore,
  onLoadMore,
}: Props<TData>) => {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | undefined>();
  const [intermediateInput, setIntermediateInput] = useState<
    | {
        rowIndex: number;
        columnId: string;
        value: string;
      }
    | undefined
  >();
  const getHoveredRowIndex = useCallback(
    () => hoveredRowIndex,
    [hoveredRowIndex]
  );
  const defaultGetCellSizeStyles = useCallback((cell: Cell<TData, unknown>) => {
    return {
      width: cell.column.columnDef.size,
    };
  }, []);
  const getCellSizeStyles =
    getCellSizeStylesFromProps ?? defaultGetCellSizeStyles;

  const table = useReactTable({
    columns,
    data,
    getRowCanExpand: getRowCanExpand ?? (() => false),
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    enableColumnResizing: true,
    enableSorting,
    manualSorting: true,
    onSortingChange,
    initialState: {
      columnPinning: columnPinning ?? {
        left: [],
        right: [],
      },
      expanded,
    },
    state: {
      sorting,
    },
    getExpandedRowModel: getExpandedRowModel(),
    meta: {
      onMoveRow,
      onCellUpdateWithRowId,
      onTextInputCellUpdate: onCellUpdate,
      onSelectInputCellUpdate: onCellUpdate,
      onMultiSelectInputCellUpdate: onCellUpdate,
      getRowStyles,
      getHoveredRowIndex,
      intermediateInput,
      setIntermediateInput,
      getIsLoading,
      getCellSizeStyles,
      getContainerModel,
    },
  });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const rows = table.getRowModel().rows;
      const fromIndex = rows.findIndex((row) => row.id === event.active.id);
      const toIndex = rows.findIndex((row) => row.id === event.over?.id);
      if (fromIndex !== -1 && toIndex !== -1) {
        onMoveRow?.(rows[fromIndex].original, fromIndex, toIndex);
      }
    },
    [onMoveRow, table]
  );

  const handleRowClick = useCallback(
    (row: RowType<TData>) => {
      onCoreRowClick?.(row);
      onRowClick?.(row.original);
    },
    [onCoreRowClick, onRowClick]
  );

  const debouncedSetHoveredRowIndex = useMemo(
    () => debounce(setHoveredRowIndex, 10, { maxWait: 50 }),
    []
  );

  const sensors = useSensors(useSensor(MouseSensor, {}));

  useEffect(() => {
    if (!table.getCanSomeRowsExpand()) {
      table.toggleAllRowsExpanded?.(false);
    }
  }, [getRowCanExpand, table]);

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      <RadixTableRoot
        variant={variant}
        size="1"
        bordered={bordered !== undefined ? bordered : variant === "ghost"}
        layout="auto"
      >
        <RadixTable.Header>
          {table.getHeaderGroups().map((headerGroup) => {
            return (
              <RadixTable.Row key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <RadixTable.ColumnHeaderCell
                      key={header.column.id}
                      style={{
                        minWidth: header.column.columnDef.minSize,
                        width: header.getSize(),
                        maxWidth: header.column.columnDef.maxSize,
                        ...getCommonPinningStyles(header.column),
                      }}
                    >
                      <Flex gap="1" align="center">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() &&
                          !header.column.getIsSorted() && (
                            <CaretSortIcon
                              style={{
                                flexShrink: 0,
                                cursor: "pointer",
                              }}
                              onClick={header.column.getToggleSortingHandler()}
                            />
                          )}
                        {{
                          asc: (
                            <ArrowUpIcon
                              style={{ flexShrink: 0, cursor: "pointer" }}
                              onClick={header.column.getToggleSortingHandler()}
                            />
                          ),
                          desc: (
                            <ArrowDownIcon
                              style={{ flexShrink: 0, cursor: "pointer" }}
                              onClick={header.column.getToggleSortingHandler()}
                            />
                          ),
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </RadixTable.ColumnHeaderCell>
                  );
                })}
              </RadixTable.Row>
            );
          })}
        </RadixTable.Header>
        <RadixTable.Body>
          <SortableContext
            items={table.getRowModel().rows.map((row) => row.id)}
            strategy={verticalListSortingStrategy}
          >
            {table.getRowModel().rows.map((row) => {
              return (
                <Fragment key={row.id}>
                  <Row
                    key={row.id}
                    style={table.options.meta?.getRowStyles?.(
                      row,
                      row.index === hoveredRowIndex
                    )}
                    onMouseEnter={() => {
                      debouncedSetHoveredRowIndex(row.index);
                    }}
                    onMouseLeave={() => {
                      debouncedSetHoveredRowIndex(undefined);
                    }}
                    onClick={() => handleRowClick(row)}
                    rowId={row.id}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <RadixTable.Cell
                          key={cell.id}
                          style={{
                            ...getCommonPinningStyles(cell.column),
                            ...getCellSizeStyles(cell),
                            ...getCommonExpandedRowStyles(row),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </RadixTable.Cell>
                      );
                    })}
                  </Row>
                  {row.getIsExpanded() && !!renderUnwrappedExpandedRow && (
                    <Row
                      key={`${row.id}-expanded`}
                      rowId={`${row.id}-expanded`}
                    >
                      {renderUnwrappedExpandedRow(row)}
                    </Row>
                  )}
                  {row.getIsExpanded() && !!renderExpandedRow && (
                    <Row
                      key={`${row.id}-expanded`}
                      rowId={`${row.id}-expanded`}
                    >
                      <RadixTable.Cell colSpan={row.getAllCells().length} p="0">
                        {renderExpandedRow(row)}
                      </RadixTable.Cell>
                    </Row>
                  )}
                </Fragment>
              );
            })}
          </SortableContext>
          {hasMore && !!onLoadMore && (
            <Loader isLoading={getIsLoading?.() ?? false} onLoad={onLoadMore} />
          )}
        </RadixTable.Body>
      </RadixTableRoot>
      {createPortal(<DragOverlay dropAnimation={null} />, document.body)}
      {getIsLoading?.() && (
        <Flex justify="center" align="center" p="4">
          <Spinner size="lg" centered />
        </Flex>
      )}
    </DndContext>
  );
};
